import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  TouchableOpacity,
  View,
  Image,
  StyleSheet,
  PanResponder,
  Animated,
  Linking,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';

const MaterialIconsX = MaterialIcons as unknown as React.ComponentType<any>;

interface ZoomableImageProps {
  imageUri: string;
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({ imageUri }) => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [scaleIndex, setScaleIndex] = useState<number>(0);
  const zoomLevels = [1, 1.25, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5];

  const position = useRef(new Animated.ValueXY()).current;
  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        position.setOffset({
          x: (position.x as any)._value,
          y: (position.y as any)._value,
        });
        position.setValue({ x: 0, y: 0 });
      },
      onPanResponderMove: Animated.event(
        [
          null,
          {
            dx: position.x,
            dy: position.y,
          },
        ],
        { useNativeDriver: false },
      ),
      onPanResponderRelease: () => {
        position.flattenOffset();
      },
    }),
  ).current;

  const handleZoomIn = () => {
    if (scaleIndex < zoomLevels.length - 1) {
      setScaleIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handleZoomOut = () => {
    if (scaleIndex > 0) {
      setScaleIndex((prevIndex) => prevIndex - 1);
      position.setValue({ x: 0, y: 0 });
    }
  };

  const handleClose = () => {
    setModalVisible(false);
    setScaleIndex(0);
    position.setValue({ x: 0, y: 0 });
  };

  const handleScroll = (event: any) => {
    if (event.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      window.addEventListener('wheel', handleScroll);
      return () => window.removeEventListener('wheel', handleScroll);
    }
  }, [scaleIndex]);

  return (
    <View>
      <TouchableOpacity
        onPress={() => {
          setModalVisible(true);
          setScaleIndex(0);
        }}
      >
        <Image
          source={{ uri: imageUri }}
          style={{
            width: 'auto',
            minWidth: 250,
            height: 200,
            padding: 20,
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
          }}
        />
      </TouchableOpacity>

      <Modal visible={modalVisible} transparent={true}>
        <View style={styles.modalContainer}>
          <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
            <MaterialIconsX name="close" size={26} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.downloadButton}
            onPress={() => {
              Linking.openURL(imageUri);
            }}
          >
            <MaterialIconsX name="download" size={26} />
          </TouchableOpacity>
          <View style={styles.zoomButtonsContainer}>
            <TouchableOpacity style={styles.zoomButton} onPress={handleZoomIn}>
              <MaterialIconsX name="zoom-in" size={26} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.zoomButton} onPress={handleZoomOut}>
              <MaterialIconsX name="zoom-out" size={26} />
            </TouchableOpacity>
          </View>
          <Animated.View
            style={[styles.imageContainer, position.getLayout()]}
            {...panResponder.panHandlers}
          >
            <Image
              source={{ uri: imageUri }}
              style={[
                styles.zoomedImage,
                { transform: [{ scale: zoomLevels[scaleIndex] }] },
              ]}
              resizeMode="contain"
            />
          </Animated.View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '90%',
    height: '90%',
  },
  zoomedImage: {
    width: '100%',
    height: '100%',
  },
  closeButton: {
    position: 'absolute',
    top: 40,
    right: 20,
    backgroundColor: '#EEF7FF',
    padding: 5,
    borderRadius: 5,
    zIndex: 2,
  },
  downloadButton: {
    position: 'absolute',
    top: 40,
    right: 70,
    backgroundColor: '#EEF7FF',
    padding: 5,
    borderRadius: 5,
    zIndex: 2,
  },

  zoomButtonsContainer: {
    position: 'absolute',
    top: 40,
    left: 20,
    flexDirection: 'row',
    justifyContent: 'center',
    zIndex: 1,
  },
  zoomButton: {
    backgroundColor: '#EEF7FF',
    padding: 5,
    borderRadius: 5,
    marginHorizontal: 10,
  },
});

export default ZoomableImage;
